import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { API } from "../api";
import PageLoader from "../components/PageLoader";
import "../styles/style_checkout.css";

// Configuration des créneaux horaires disponibles
const TIME_SLOTS = [
  { id: "morning", label: "Matin", description: "9h - 12h", icon: "☀️" },
  { id: "early_afternoon", label: "Début d'après-midi", description: "12h - 15h", icon: "🌤️" },
  { id: "late_afternoon", label: "Fin d'après-midi", description: "15h - 18h", icon: "🌅" },
  { id: "evening", label: "Soirée", description: "18h - 20h", icon: "🌙" },
];

// Options supplémentaires disponibles
const EXTRA_OPTIONS = {
  ribbon: {
    id: "ribbon",
    label: "Ruban personnalisé",
    description: "Ajoutez un ruban avec votre message",
    icon: "🎀",
    price: 3.50,
    hasText: true,
    textPlaceholder: "Ex: Joyeux anniversaire !",
    textMaxLength: 50,
  },
  card: {
    id: "card",
    label: "Carte message",
    description: "Une jolie carte avec votre texte",
    icon: "💌",
    price: 2.00,
    hasText: true,
    textPlaceholder: "Votre message personnel...",
    textMaxLength: 150,
  },
  gift_wrap: {
    id: "gift_wrap",
    label: "Emballage cadeau premium",
    description: "Papier de soie et nœud satin",
    icon: "🎁",
    price: 5.00,
    hasText: false,
  },
  vase: {
    id: "vase",
    label: "Vase assorti",
    description: "Un vase élégant pour votre bouquet",
    icon: "🏺",
    price: 12.00,
    hasText: false,
  },
};

// Configuration des étiquettes
const LABEL_CONFIG = {
  firstFree: true, // Première étiquette offerte
  pricePerLabel: 1.50, // Prix par étiquette supplémentaire
  maxLabels: 5,
};

// Frais de livraison
const DELIVERY_CONFIG = {
  baseFee: 6.90,
  freeThreshold: 50, // Livraison gratuite à partir de ce montant
  clickCollectFee: 0,
};

// Données de démonstration pour le checkout
const DEMO_CART_ITEMS = [
  {
    itemId: "1-M",
    product: { id: 1, libelle: "Bouquet Romance", tva: 20.00 },
    variant: { taille_code: "M", taille_libelle: "Moyen", prixHT: 35.00 },
    quantity: 2,
    bouquetType: null,
    selected: true,
  },
  {
    itemId: "99-L-bulle",
    product: { id: 99, libelle: "Bouquet Personnalisable", tva: 20.00 },
    variant: { taille_code: "L", taille_libelle: "Grand", prixHT: 60.00 },
    quantity: 1,
    bouquetType: "bulle",
    selected: true,
  },
  {
    itemId: "3-M",
    product: { id: 3, libelle: "Orchidée Élégance", tva: 20.00 },
    variant: { taille_code: "M", taille_libelle: "Moyen", prixHT: 45.00 },
    quantity: 1,
    bouquetType: null,
    selected: true,
  },
];

const DEMO_ADDRESSES = [
  {
    id: 1,
    street_name: "15 rue des Roses",
    complement: "Appartement 3B",
    postal_code: "75001",
    city: "Paris",
    country: "FR",
    is_default: true,
  },
  {
    id: 2,
    street_name: "42 avenue des Fleurs",
    complement: "",
    postal_code: "75008",
    city: "Paris",
    country: "FR",
    is_default: false,
  },
];

export default function Checkout() {
  // MODE DÉMO : true pour voir le mockup, false pour utiliser le vrai panier
  const [demoMode] = useState(true);
  
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { cartItems: realCartItems, totals: realTotals, calculateTTC, clearCart } = useCart();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Mode de livraison
  const [deliveryMode, setDeliveryMode] = useState("delivery"); // "delivery" ou "click_collect"

  // Adresses
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [saveNewAddress, setSaveNewAddress] = useState(true);
  const [savingAddress, setSavingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    country: "France",
    postal_code: "",
    city: "",
    street_name: "",
    complement: "",
  });

  // Date et créneau
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");

  // Options supplémentaires
  const [selectedExtras, setSelectedExtras] = useState({});
  const [extraTexts, setExtraTexts] = useState({});

  // Étiquettes
  const [labelCount, setLabelCount] = useState(0);
  const [labelTexts, setLabelTexts] = useState([]);

  // Message d'erreur
  const [error, setError] = useState("");

  // Fonction pour calculer TTC
  const calcTTC = (prixHT, tva) => prixHT * (1 + tva / 100);

  // Données du panier (démo ou réel)
  const cartItems = demoMode ? DEMO_CART_ITEMS : realCartItems;

  // Totaux calculés pour le mode démo
  const demoTotals = useMemo(() => {
    const selectedItems = DEMO_CART_ITEMS.filter(item => item.selected);
    let subtotalSelected = 0;
    let selectedCount = 0;
    let selectedQuantity = 0;

    selectedItems.forEach(item => {
      const ttc = calcTTC(item.variant.prixHT, item.product.tva);
      subtotalSelected += ttc * item.quantity;
      selectedCount++;
      selectedQuantity += item.quantity;
    });

    return { subtotalSelected, selectedCount, selectedQuantity };
  }, []);

  const totals = demoMode ? demoTotals : realTotals;

  // Charger les adresses de l'utilisateur
  useEffect(() => {
    if (demoMode) {
      // En mode démo, utiliser les adresses fictives
      setAddresses(DEMO_ADDRESSES);
      setSelectedAddressId(DEMO_ADDRESSES[0].id);
      setLoading(false);
    } else if (user) {
      fetchAddresses();
    } else {
      setLoading(false);
    }
  }, [user, demoMode]);

  // Rediriger si panier vide (seulement en mode réel)
  useEffect(() => {
    if (!demoMode && !loading && realCartItems.length === 0) {
      navigate("/panier");
    }
  }, [loading, realCartItems, navigate, demoMode]);

  const fetchAddresses = async () => {
    if (!user) return;
    setAddressesLoading(true);
    try {
      const res = await API.get("user_addresses", { user_id: user.id });
      const addressList = Array.isArray(res) ? res : [];
      setAddresses(addressList);
      
      // Sélectionner l'adresse par défaut
      const defaultAddress = addressList.find(addr => addr.is_default);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else if (addressList.length > 0) {
        setSelectedAddressId(addressList[0].id);
      }
      
      // Si aucune adresse, afficher le formulaire
      if (addressList.length === 0) {
        setShowNewAddressForm(true);
      }
    } catch (err) {
      console.error("Erreur chargement adresses:", err);
      setError("Impossible de charger vos adresses");
    } finally {
      setAddressesLoading(false);
      setLoading(false);
    }
  };

  // Enregistrer une nouvelle adresse dans le profil
  const handleSaveNewAddress = async () => {
    if (!user) return false;
    
    // Validation
    if (!newAddress.street_name || !newAddress.postal_code || !newAddress.city) {
      setError("Veuillez remplir tous les champs obligatoires de l'adresse");
      return false;
    }

    setSavingAddress(true);
    try {
      await API.post("user_addresses", {
        user_id: user.id,
        country: newAddress.country || "France",
        postal_code: newAddress.postal_code,
        city: newAddress.city,
        street_name: newAddress.street_name,
        complement: newAddress.complement || "",
        is_default: addresses.length === 0, // Première adresse = par défaut
      }, {});

      // Recharger les adresses
      await fetchAddresses();
      
      // Réinitialiser le formulaire
      setNewAddress({
        country: "France",
        postal_code: "",
        city: "",
        street_name: "",
        complement: "",
      });
      setShowNewAddressForm(false);
      
      return true;
    } catch (err) {
      console.error("Erreur enregistrement adresse:", err);
      setError(err.message || "Impossible d'enregistrer l'adresse");
      return false;
    } finally {
      setSavingAddress(false);
    }
  };

  // Calculer les dates disponibles (7 prochains jours, sauf dimanche)
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Exclure les dimanches (0 = dimanche)
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split("T")[0],
          label: date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }),
          isToday: i === 0,
          isTomorrow: i === 1,
        });
      }
      
      if (dates.length >= 7) break;
    }
    
    return dates;
  }, []);

  // Calculer le prix total
  const orderTotals = useMemo(() => {
    // Sous-total des produits sélectionnés
    const productsSubtotal = totals.subtotalSelected;

    // Frais de livraison
    let deliveryFee = 0;
    if (deliveryMode === "delivery") {
      deliveryFee = productsSubtotal >= DELIVERY_CONFIG.freeThreshold ? 0 : DELIVERY_CONFIG.baseFee;
    }

    // Options supplémentaires
    let extrasTotal = 0;
    Object.keys(selectedExtras).forEach((key) => {
      if (selectedExtras[key] && EXTRA_OPTIONS[key]) {
        extrasTotal += EXTRA_OPTIONS[key].price;
      }
    });

    // Étiquettes
    let labelsTotal = 0;
    if (labelCount > 0) {
      const paidLabels = LABEL_CONFIG.firstFree ? Math.max(0, labelCount - 1) : labelCount;
      labelsTotal = paidLabels * LABEL_CONFIG.pricePerLabel;
    }

    const total = productsSubtotal + deliveryFee + extrasTotal + labelsTotal;

    return {
      productsSubtotal,
      deliveryFee,
      extrasTotal,
      labelsTotal,
      total,
    };
  }, [totals.subtotalSelected, deliveryMode, selectedExtras, labelCount]);

  // Formater le prix
  const formatPrice = (price) => price.toFixed(2).replace(".", ",");

  // Gérer les options supplémentaires
  const toggleExtra = (extraId) => {
    setSelectedExtras((prev) => ({
      ...prev,
      [extraId]: !prev[extraId],
    }));
    if (!selectedExtras[extraId]) {
      setExtraTexts((prev) => ({ ...prev, [extraId]: "" }));
    }
  };

  // Gérer le texte des options
  const handleExtraTextChange = (extraId, text) => {
    const maxLength = EXTRA_OPTIONS[extraId]?.textMaxLength || 100;
    setExtraTexts((prev) => ({
      ...prev,
      [extraId]: text.slice(0, maxLength),
    }));
  };

  // Gérer les étiquettes
  const handleLabelCountChange = (count) => {
    const newCount = Math.max(0, Math.min(count, LABEL_CONFIG.maxLabels));
    setLabelCount(newCount);
    
    // Ajuster le tableau des textes
    setLabelTexts((prev) => {
      const newTexts = [...prev];
      while (newTexts.length < newCount) newTexts.push("");
      return newTexts.slice(0, newCount);
    });
  };

  const handleLabelTextChange = (index, text) => {
    setLabelTexts((prev) => {
      const newTexts = [...prev];
      newTexts[index] = text.slice(0, 30);
      return newTexts;
    });
  };

  // Gérer la nouvelle adresse
  const handleNewAddressChange = (e) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({ ...prev, [name]: value }));
  };

  // Valider l'étape actuelle
  const validateStep = (step) => {
    setError("");
    
    switch (step) {
      case 1: // Mode de livraison et adresse
        if (deliveryMode === "delivery") {
          if (showNewAddressForm) {
            if (!newAddress.street_name || !newAddress.postal_code || !newAddress.city) {
              setError("Veuillez remplir tous les champs obligatoires de l'adresse");
              return false;
            }
          } else if (!selectedAddressId) {
            setError("Veuillez sélectionner une adresse de livraison");
            return false;
          }
        }
        return true;

      case 2: // Date et créneau
        if (!selectedDate) {
          setError("Veuillez sélectionner une date");
          return false;
        }
        if (!selectedTimeSlot) {
          setError("Veuillez sélectionner un créneau horaire");
          return false;
        }
        return true;

      case 3: // Options (pas de validation obligatoire)
        return true;

      default:
        return true;
    }
  };

  // Navigation entre les étapes
  const goToNextStep = async () => {
    if (!validateStep(currentStep)) return;
    
    // Si on passe de l'étape 1 et qu'on a une nouvelle adresse à enregistrer
    if (currentStep === 1 && showNewAddressForm && saveNewAddress && deliveryMode === "delivery") {
      const saved = await handleSaveNewAddress();
      if (!saved) return; // Erreur lors de l'enregistrement
    }
    
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const goToPreviousStep = () => {
    setError("");
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Soumettre la commande
  const handleSubmitOrder = async () => {
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    setError("");

    try {
      // Préparer les données de la commande
      const orderData = {
        items: cartItems.filter(item => item.selected).map(item => ({
          productId: item.product.id,
          variantCode: item.variant.taille_code,
          quantity: item.quantity,
          bouquetType: item.bouquetType,
          unitPrice: calcTTC(item.variant.prixHT, item.product.tva),
        })),
        deliveryMode,
        deliveryAddress: deliveryMode === "delivery" 
          ? (showNewAddressForm ? newAddress : addresses.find(a => a.id === selectedAddressId))
          : null,
        saveNewAddress: showNewAddressForm && saveNewAddress,
        deliveryDate: selectedDate,
        timeSlot: selectedTimeSlot,
        extras: Object.keys(selectedExtras)
          .filter(key => selectedExtras[key])
          .map(key => ({
            type: key,
            text: extraTexts[key] || null,
          })),
        labels: labelTexts.filter(t => t.trim() !== ""),
        totals: orderTotals,
      };

      // TODO: Envoyer la commande à l'API
      console.log("Commande à envoyer:", orderData);
      
      // Simuler un délai de traitement
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Ajouter les noms des produits pour l'affichage
      const orderDataWithNames = {
        ...orderData,
        items: orderData.items.map((item, index) => ({
          ...item,
          productName: cartItems.filter(c => c.selected)[index]?.product.libelle,
          variantLabel: cartItems.filter(c => c.selected)[index]?.variant.taille_libelle,
        })),
      };

      // Vider le panier et rediriger vers la page de confirmation
      // clearCart(); // Décommenter en production
      navigate("/commande-confirmee", { 
        state: { 
          orderData: orderDataWithNames,
          orderNumber: `FL-${Date.now().toString(36).toUpperCase()}`,
        }
      });

    } catch (err) {
      setError("Une erreur est survenue lors de la validation de votre commande");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Obtenir l'adresse sélectionnée
  const selectedAddress = useMemo(() => {
    return addresses.find(a => a.id === selectedAddressId);
  }, [addresses, selectedAddressId]);

  if (loading || authLoading) return <PageLoader label="Préparation de votre commande" />;

  // Rediriger vers connexion si non connecté (sauf en mode démo)
  if (!demoMode && !user) {
    return (
      <div className="checkout-page">
        <div className="checkout-auth-required">
          <div className="auth-required-content">
            <ion-icon name="lock-closed-outline"></ion-icon>
            <h2>Connexion requise</h2>
            <p>Veuillez vous connecter pour finaliser votre commande</p>
            <Link to="/auth" state={{ defaultForm: "signin", redirectTo: "/checkout" }} className="btn-login">
              <ion-icon name="log-in-outline"></ion-icon>
              Se connecter
            </Link>
            <Link to="/panier" className="btn-back">
              Retour au panier
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {/* Header */}
      <div className="checkout-header">
        <div className="checkout-header-content">
          <Link to="/panier" className="back-link">
            <ion-icon name="arrow-back-outline"></ion-icon>
            Retour au panier
          </Link>
          <h1 className="checkout-title">Finaliser ma commande</h1>
        </div>
      </div>

      {/* Stepper */}
      <div className="checkout-stepper">
        <div className={`step ${currentStep >= 1 ? "active" : ""} ${currentStep > 1 ? "completed" : ""}`}>
          <div className="step-number">
            {currentStep > 1 ? <ion-icon name="checkmark-outline"></ion-icon> : "1"}
          </div>
          <span className="step-label">Livraison</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${currentStep >= 2 ? "active" : ""} ${currentStep > 2 ? "completed" : ""}`}>
          <div className="step-number">
            {currentStep > 2 ? <ion-icon name="checkmark-outline"></ion-icon> : "2"}
          </div>
          <span className="step-label">Date & Heure</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${currentStep >= 3 ? "active" : ""} ${currentStep > 3 ? "completed" : ""}`}>
          <div className="step-number">
            {currentStep > 3 ? <ion-icon name="checkmark-outline"></ion-icon> : "3"}
          </div>
          <span className="step-label">Options</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step ${currentStep >= 4 ? "active" : ""}`}>
          <div className="step-number">4</div>
          <span className="step-label">Confirmation</span>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="checkout-content">
        <div className="checkout-main">
          {/* Message d'erreur */}
          {error && (
            <div className="checkout-error">
              <ion-icon name="alert-circle-outline"></ion-icon>
              <span>{error}</span>
            </div>
          )}

          {/* Étape 1: Mode de livraison */}
          {currentStep === 1 && (
            <div className="checkout-step-content">
              <h2 className="step-title">
                <ion-icon name="location-outline"></ion-icon>
                Mode de livraison
              </h2>

              {/* Choix du mode */}
              <div className="delivery-mode-selector">
                <button
                  className={`delivery-mode-btn ${deliveryMode === "delivery" ? "selected" : ""}`}
                  onClick={() => setDeliveryMode("delivery")}
                >
                  <div className="mode-icon">🚚</div>
                  <div className="mode-info">
                    <span className="mode-title">Livraison à domicile</span>
                    <span className="mode-price">
                      {orderTotals.productsSubtotal >= DELIVERY_CONFIG.freeThreshold 
                        ? "Gratuit" 
                        : `${formatPrice(DELIVERY_CONFIG.baseFee)} €`}
                    </span>
                  </div>
                  {orderTotals.productsSubtotal >= DELIVERY_CONFIG.freeThreshold && (
                    <span className="mode-badge">Offert</span>
                  )}
                </button>

                <button
                  className={`delivery-mode-btn ${deliveryMode === "click_collect" ? "selected" : ""}`}
                  onClick={() => setDeliveryMode("click_collect")}
                >
                  <div className="mode-icon">🏪</div>
                  <div className="mode-info">
                    <span className="mode-title">Click & Collect</span>
                    <span className="mode-price">Gratuit</span>
                  </div>
                  <span className="mode-desc">Retrait en boutique</span>
                </button>
              </div>

              {/* Adresse de livraison */}
              {deliveryMode === "delivery" && (
                <div className="delivery-address-section">
                  <h3 className="section-subtitle">
                    <ion-icon name="home-outline"></ion-icon>
                    Adresse de livraison
                  </h3>

                  {/* Liste des adresses existantes */}
                  {!showNewAddressForm && (
                    <>
                      {addressesLoading ? (
                        <div className="loading-addresses">Chargement des adresses...</div>
                      ) : addresses.length > 0 ? (
                        <div className="address-list">
                          {addresses.map((addr) => (
                            <div
                              key={addr.id}
                              className={`address-card ${selectedAddressId === addr.id ? "selected" : ""}`}
                              onClick={() => setSelectedAddressId(addr.id)}
                            >
                              <div className="address-radio">
                                <input
                                  type="radio"
                                  name="delivery_address"
                                  checked={selectedAddressId === addr.id}
                                  onChange={() => setSelectedAddressId(addr.id)}
                                />
                                <span className="radio-checkmark"></span>
                              </div>
                              <div className="address-details">
                                <span className="address-street">{addr.street_name}</span>
                                {addr.complement && (
                                  <span className="address-complement">{addr.complement}</span>
                                )}
                                <span className="address-city">
                                  {addr.postal_code} {addr.city}
                                </span>
                              </div>
                              {addr.is_default && (
                                <span className="address-badge">Par défaut</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-addresses">Aucune adresse enregistrée</p>
                      )}

                      <button
                        className="btn-new-address"
                        onClick={() => setShowNewAddressForm(true)}
                      >
                        <ion-icon name="add-circle-outline"></ion-icon>
                        Utiliser une nouvelle adresse
                      </button>
                    </>
                  )}

                  {/* Formulaire nouvelle adresse */}
                  {showNewAddressForm && (
                    <div className="new-address-form">
                      <div className="form-header">
                        <h4>Nouvelle adresse</h4>
                        {addresses.length > 0 && (
                          <button
                            className="btn-cancel-new"
                            onClick={() => setShowNewAddressForm(false)}
                          >
                            Annuler
                          </button>
                        )}
                      </div>

                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label htmlFor="street_name">Adresse *</label>
                          <input
                            type="text"
                            id="street_name"
                            name="street_name"
                            value={newAddress.street_name}
                            onChange={handleNewAddressChange}
                            placeholder="10 rue des Fleurs"
                          />
                        </div>

                        <div className="form-group full-width">
                          <label htmlFor="complement">Complément</label>
                          <input
                            type="text"
                            id="complement"
                            name="complement"
                            value={newAddress.complement}
                            onChange={handleNewAddressChange}
                            placeholder="Appartement, bâtiment, code..."
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="postal_code">Code postal *</label>
                          <input
                            type="text"
                            id="postal_code"
                            name="postal_code"
                            value={newAddress.postal_code}
                            onChange={handleNewAddressChange}
                            placeholder="75001"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="city">Ville *</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={newAddress.city}
                            onChange={handleNewAddressChange}
                            placeholder="Paris"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="country">Pays</label>
                          <input
                            type="text"
                            id="country"
                            name="country"
                            value={newAddress.country}
                            onChange={handleNewAddressChange}
                            placeholder="FR"
                          />
                        </div>
                      </div>

                      <label className="save-address-checkbox">
                        <input
                          type="checkbox"
                          checked={saveNewAddress}
                          onChange={(e) => setSaveNewAddress(e.target.checked)}
                        />
                        <span className="checkbox-mark"></span>
                        <span>Enregistrer cette adresse dans mon profil</span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Info Click & Collect */}
              {deliveryMode === "click_collect" && (
                <div className="click-collect-info">
                  <div className="store-card">
                    <div className="store-icon">🌸</div>
                    <div className="store-details">
                      <h4>L'Atelier Floral</h4>
                      <p className="store-address">12 rue des Jardins, 75001 Paris</p>
                      <p className="store-hours">
                        <ion-icon name="time-outline"></ion-icon>
                        Lun-Sam: 9h-19h
                      </p>
                    </div>
                  </div>
                  <p className="collect-notice">
                    <ion-icon name="information-circle-outline"></ion-icon>
                    Vous recevrez un SMS dès que votre commande sera prête
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Étape 2: Date et créneau */}
          {currentStep === 2 && (
            <div className="checkout-step-content">
              <h2 className="step-title">
                <ion-icon name="calendar-outline"></ion-icon>
                Date et créneau horaire
              </h2>

              {/* Sélection de la date */}
              <div className="date-selector">
                <h3 className="section-subtitle">Choisissez une date</h3>
                <div className="date-grid">
                  {availableDates.map((date) => (
                    <button
                      key={date.value}
                      className={`date-btn ${selectedDate === date.value ? "selected" : ""}`}
                      onClick={() => setSelectedDate(date.value)}
                    >
                      <span className="date-day">
                        {date.isTomorrow ? "Demain" : date.label.split(" ")[0]}
                      </span>
                      <span className="date-number">
                        {new Date(date.value).getDate()}
                      </span>
                      <span className="date-month">
                        {new Date(date.value).toLocaleDateString("fr-FR", { month: "short" })}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sélection du créneau */}
              <div className="time-slot-selector">
                <h3 className="section-subtitle">Choisissez un créneau</h3>
                <div className="time-slot-grid">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot.id}
                      className={`time-slot-btn ${selectedTimeSlot === slot.id ? "selected" : ""}`}
                      onClick={() => setSelectedTimeSlot(slot.id)}
                    >
                      <span className="slot-icon">{slot.icon}</span>
                      <span className="slot-label">{slot.label}</span>
                      <span className="slot-time">{slot.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Étape 3: Options supplémentaires */}
          {currentStep === 3 && (
            <div className="checkout-step-content">
              <h2 className="step-title">
                <ion-icon name="gift-outline"></ion-icon>
                Personnalisez votre commande
              </h2>

              {/* Options extras */}
              <div className="extras-section">
                <h3 className="section-subtitle">Options supplémentaires</h3>
                <div className="extras-grid">
                  {Object.values(EXTRA_OPTIONS).map((extra) => (
                    <div
                      key={extra.id}
                      className={`extra-card ${selectedExtras[extra.id] ? "selected" : ""}`}
                    >
                      <div className="extra-header" onClick={() => toggleExtra(extra.id)}>
                        <div className="extra-checkbox">
                          <input
                            type="checkbox"
                            checked={!!selectedExtras[extra.id]}
                            onChange={() => toggleExtra(extra.id)}
                          />
                          <span className="checkbox-mark"></span>
                        </div>
                        <span className="extra-icon">{extra.icon}</span>
                        <div className="extra-info">
                          <span className="extra-label">{extra.label}</span>
                          <span className="extra-desc">{extra.description}</span>
                        </div>
                        <span className="extra-price">+{formatPrice(extra.price)} €</span>
                      </div>

                      {selectedExtras[extra.id] && extra.hasText && (
                        <div className="extra-text-input">
                          <textarea
                            placeholder={extra.textPlaceholder}
                            value={extraTexts[extra.id] || ""}
                            onChange={(e) => handleExtraTextChange(extra.id, e.target.value)}
                            maxLength={extra.textMaxLength}
                          />
                          <span className="char-count">
                            {(extraTexts[extra.id] || "").length}/{extra.textMaxLength}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Étiquettes */}
              <div className="labels-section">
                <h3 className="section-subtitle">
                  <span>Étiquettes personnalisées</span>
                  {LABEL_CONFIG.firstFree && (
                    <span className="label-promo">🎁 1ère offerte !</span>
                  )}
                </h3>
                <p className="labels-info">
                  Ajoutez des étiquettes avec un nom ou un petit message
                  {!LABEL_CONFIG.firstFree && ` (${formatPrice(LABEL_CONFIG.pricePerLabel)} € / étiquette)`}
                  {LABEL_CONFIG.firstFree && `, puis ${formatPrice(LABEL_CONFIG.pricePerLabel)} € par étiquette supplémentaire`}
                </p>

                <div className="label-count-selector">
                  <button
                    className="count-btn"
                    onClick={() => handleLabelCountChange(labelCount - 1)}
                    disabled={labelCount <= 0}
                  >
                    <ion-icon name="remove-outline"></ion-icon>
                  </button>
                  <span className="count-value">{labelCount}</span>
                  <button
                    className="count-btn"
                    onClick={() => handleLabelCountChange(labelCount + 1)}
                    disabled={labelCount >= LABEL_CONFIG.maxLabels}
                  >
                    <ion-icon name="add-outline"></ion-icon>
                  </button>
                  <span className="count-max">/ {LABEL_CONFIG.maxLabels} max</span>
                </div>

                {labelCount > 0 && (
                  <div className="label-inputs">
                    {Array.from({ length: labelCount }).map((_, index) => (
                      <div key={index} className="label-input-row">
                        <span className="label-number">
                          🏷️ Étiquette {index + 1}
                          {LABEL_CONFIG.firstFree && index === 0 && (
                            <span className="free-badge">Offerte</span>
                          )}
                        </span>
                        <input
                          type="text"
                          placeholder="Texte de l'étiquette..."
                          value={labelTexts[index] || ""}
                          onChange={(e) => handleLabelTextChange(index, e.target.value)}
                          maxLength={30}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Étape 4: Récapitulatif */}
          {currentStep === 4 && (
            <div className="checkout-step-content">
              <h2 className="step-title">
                <ion-icon name="checkmark-circle-outline"></ion-icon>
                Récapitulatif de votre commande
              </h2>

              {/* Récap livraison */}
              <div className="recap-section">
                <div className="recap-header">
                  <h3>
                    <ion-icon name="location-outline"></ion-icon>
                    {deliveryMode === "delivery" ? "Livraison" : "Retrait en boutique"}
                  </h3>
                  <button className="btn-edit" onClick={() => setCurrentStep(1)}>
                    Modifier
                  </button>
                </div>
                {deliveryMode === "delivery" ? (
                  <div className="recap-content">
                    {showNewAddressForm ? (
                      <p>
                        {newAddress.street_name}<br />
                        {newAddress.complement && <>{newAddress.complement}<br /></>}
                        {newAddress.postal_code} {newAddress.city}
                      </p>
                    ) : selectedAddress ? (
                      <p>
                        {selectedAddress.street_name}<br />
                        {selectedAddress.complement && <>{selectedAddress.complement}<br /></>}
                        {selectedAddress.postal_code} {selectedAddress.city}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="recap-content">
                    <p>L'Atelier Floral<br />12 rue des Jardins, 75001 Paris</p>
                  </div>
                )}
              </div>

              {/* Récap date/heure */}
              <div className="recap-section">
                <div className="recap-header">
                  <h3>
                    <ion-icon name="calendar-outline"></ion-icon>
                    Date et créneau
                  </h3>
                  <button className="btn-edit" onClick={() => setCurrentStep(2)}>
                    Modifier
                  </button>
                </div>
                <div className="recap-content">
                  <p>
                    {new Date(selectedDate).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}
                    {" — "}
                    {TIME_SLOTS.find(s => s.id === selectedTimeSlot)?.label}
                    {" ("}
                    {TIME_SLOTS.find(s => s.id === selectedTimeSlot)?.description}
                    {")"}
                  </p>
                </div>
              </div>

              {/* Récap options */}
              {(Object.values(selectedExtras).some(v => v) || labelCount > 0) && (
                <div className="recap-section">
                  <div className="recap-header">
                    <h3>
                      <ion-icon name="gift-outline"></ion-icon>
                      Options
                    </h3>
                    <button className="btn-edit" onClick={() => setCurrentStep(3)}>
                      Modifier
                    </button>
                  </div>
                  <div className="recap-content">
                    <ul className="recap-options-list">
                      {Object.keys(selectedExtras)
                        .filter(key => selectedExtras[key])
                        .map(key => (
                          <li key={key}>
                            {EXTRA_OPTIONS[key].icon} {EXTRA_OPTIONS[key].label}
                            {extraTexts[key] && <span className="option-text">: "{extraTexts[key]}"</span>}
                            <span className="option-price">+{formatPrice(EXTRA_OPTIONS[key].price)} €</span>
                          </li>
                        ))}
                      {labelCount > 0 && (
                        <li>
                          🏷️ {labelCount} étiquette{labelCount > 1 ? "s" : ""}
                          <span className="option-price">
                            {LABEL_CONFIG.firstFree && labelCount === 1 
                              ? "Offert" 
                              : `+${formatPrice(orderTotals.labelsTotal)} €`}
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Récap articles */}
              <div className="recap-section">
                <div className="recap-header">
                  <h3>
                    <ion-icon name="cart-outline"></ion-icon>
                    Articles ({totals.selectedCount})
                  </h3>
                  <Link to="/panier" className="btn-edit">
                    Modifier
                  </Link>
                </div>
                <div className="recap-items">
                  {cartItems.filter(item => item.selected).map(item => (
                    <div key={item.itemId} className="recap-item">
                      <span className="item-qty">{item.quantity}x</span>
                      <span className="item-name">
                        {item.product.libelle}
                        <small> ({item.variant.taille_libelle})</small>
                      </span>
                      <span className="item-price">
                        {formatPrice(calcTTC(item.variant.prixHT, item.product.tva) * item.quantity)} €
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="checkout-navigation">
            {currentStep > 1 && (
              <button className="btn-prev" onClick={goToPreviousStep} disabled={savingAddress}>
                <ion-icon name="arrow-back-outline"></ion-icon>
                Retour
              </button>
            )}
            
            {currentStep < 4 ? (
              <button className="btn-next" onClick={goToNextStep} disabled={savingAddress}>
                {savingAddress ? (
                  <>Enregistrement...</>
                ) : (
                  <>
                    Continuer
                    <ion-icon name="arrow-forward-outline"></ion-icon>
                  </>
                )}
              </button>
            ) : (
              <button 
                className="btn-submit" 
                onClick={handleSubmitOrder}
                disabled={submitting}
              >
                {submitting ? (
                  <>Traitement en cours...</>
                ) : (
                  <>
                    <ion-icon name="card-outline"></ion-icon>
                    Payer {formatPrice(orderTotals.total)} €
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Résumé latéral */}
        <div className="checkout-sidebar">
          <div className="sidebar-card">
            <h3 className="sidebar-title">Votre commande</h3>

            {/* Liste des articles */}
            <div className="sidebar-items">
              {cartItems.filter(item => item.selected).slice(0, 3).map(item => (
                <div key={item.itemId} className="sidebar-item">
                  <span className="item-qty">{item.quantity}x</span>
                  <span className="item-name">{item.product.libelle}</span>
                  <span className="item-price">
                    {formatPrice(calcTTC(item.variant.prixHT, item.product.tva) * item.quantity)} €
                  </span>
                </div>
              ))}
              {totals.selectedCount > 3 && (
                <div className="sidebar-more">
                  + {totals.selectedCount - 3} autre{totals.selectedCount - 3 > 1 ? "s" : ""} article{totals.selectedCount - 3 > 1 ? "s" : ""}
                </div>
              )}
            </div>

            <div className="sidebar-divider"></div>

            {/* Totaux */}
            <div className="sidebar-totals">
              <div className="total-row">
                <span>Sous-total</span>
                <span>{formatPrice(orderTotals.productsSubtotal)} €</span>
              </div>

              <div className="total-row">
                <span>Livraison</span>
                <span className={orderTotals.deliveryFee === 0 ? "free" : ""}>
                  {orderTotals.deliveryFee === 0 ? "Offerte" : `${formatPrice(orderTotals.deliveryFee)} €`}
                </span>
              </div>

              {orderTotals.extrasTotal > 0 && (
                <div className="total-row">
                  <span>Options</span>
                  <span>+{formatPrice(orderTotals.extrasTotal)} €</span>
                </div>
              )}

              {orderTotals.labelsTotal > 0 && (
                <div className="total-row">
                  <span>Étiquettes</span>
                  <span>+{formatPrice(orderTotals.labelsTotal)} €</span>
                </div>
              )}

              <div className="sidebar-divider"></div>

              <div className="total-row total-final">
                <span>Total TTC</span>
                <span>{formatPrice(orderTotals.total)} €</span>
              </div>
            </div>

            {/* Garanties */}
            <div className="sidebar-guarantees">
              <div className="guarantee">
                <ion-icon name="shield-checkmark-outline"></ion-icon>
                <span>Paiement sécurisé</span>
              </div>
              <div className="guarantee">
                <ion-icon name="leaf-outline"></ion-icon>
                <span>Fleurs fraîches garanties</span>
              </div>
              <div className="guarantee">
                <ion-icon name="heart-outline"></ion-icon>
                <span>Satisfaction garantie</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
